import type { CalculationSettings } from '../config/calculationSettings';
import type { IncomeBreakdown, IncomeStream } from '../types';

export interface IncomeInput {
  primary: IncomeStream;
  coapplicant: IncomeStream | null;
  settings: CalculationSettings;
}

// Bonus is shaded at the same rate as non-AUD base income at the global
// indicative tier. (Per-lender bonus shading is applied later by
// `pipeline/lenders.ts` using `LenderPolicy.bonusSalaryShade`.)
const GLOBAL_BONUS_SHADE = 0.8;

/**
 * All monetary fields on `IncomeStream` are AUD already (the mapper does
 * the FX conversion). This stage sums them and applies the global
 * indicative shading on base + bonus.
 */
export function computeIncome(input: IncomeInput): IncomeBreakdown {
  const c = input.coapplicant;

  const baseAud = input.primary.baseSalary + (c?.baseSalary ?? 0);
  const bonusAud = input.primary.bonus + (c?.bonus ?? 0);
  const allowanceAud = input.primary.allowance + (c?.allowance ?? 0);
  const commissionAud = input.primary.commission + (c?.commission ?? 0);
  const stockAud = input.primary.stock + (c?.stock ?? 0);

  const grossAud = baseAud + bonusAud + allowanceAud + commissionAud + stockAud;

  const baseShade =
    input.primary.currency === 'AUD'
      ? 1.0
      : input.settings.globalBaseShadeNonAud;
  const bankIncome = baseAud * baseShade + bonusAud * GLOBAL_BONUS_SHADE;

  return {
    baseAud,
    bonusAud,
    allowanceAud,
    commissionAud,
    stockAud,
    grossAud,
    bankIncome,
  };
}
